// This function generates a random salt of a specified length using alphanumeric characters.

// How It Works:
// Characters Used: A mix of uppercase letters, lowercase letters, and numbers.
// Length of Salt: Determined by the length parameter.
// Randomization: A character is randomly chosen from the character set for each iteration of the loop.
const generateSalt = (length) => {
  let salt = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    salt += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return salt;
};

// apiEncrypt
// This function encrypts an input string by appending a salt, encoding the result in Base64, and replacing = characters with a custom sequence (123).

// Steps:
// Generate a Salt: Calls generateSalt(4) to create a 4-character salt.
// Append Salt: Concatenates the input with the salt, separated by ||.
// Example: If the input is "mySecret", and the salt is "a7Gh", the result is "mySecret||a7Gh".
// Base64 Encode: Converts the string to a Base64-encoded format.
// Example: "mySecret||a7Gh" → "bXlTZWNyZXR8fGE3R2g=".
// Custom Replace: Replaces = in the Base64 string with 123.
// Example: "bXlTZWNyZXR8fGE3R2g=" → "bXlTZWNyZXR8fGE3R2g123"

export const apiEncrypt = (input, useSalt = "1") => {
  try {
    // Generate a 4-character random salt
    const salt = generateSalt(4);

    // Append salt to the input
    let inputWithSalt = `${input}||${salt}`;
    if (useSalt !== "1") {
      inputWithSalt = `${input}`;
    }

    // Base64 encoding
    let encryptedData = btoa(inputWithSalt);

    // Custom replacement of '=' with '123'
    encryptedData = encryptedData.replace(/=/g, "123");

    return encryptedData; // Return the encrypted string
  } catch (error) {
    console.error("Encryption failed:", error);
    return null; // Return null in case of an error
  }
};

// apiDecrypt
// - This function is focused solely on decrypting an encrypted string using custom logic:

// - It replaces your custom placeholder (123) with the Base64 padding symbol (=).
// - It decodes the Base64-encoded string.
// - It extracts the original value by splitting the decoded data on || (removing the salt).
// Use apiDecrypt when you already have an encrypted string and want to decrypt it.
// Decryption function
export const apiDecrypt = (input) => {
  try {
    //  handle null or invalid input gracefully.
    if (!input) throw new Error("Input for decryption is null or undefined.");

    // Replace custom encoding back
    const encryptedData = input.replace(/123/g, "=");

    // Decode Base64
    const decodedData = atob(encryptedData);

    console.log("Decoded Data:", decodedData, input);
    // Split the decoded data to get the original value and salt
    const decryptedArray = decodedData.split("||");

    if (decryptedArray.length < 2) {
      console.error(
        "Decryption failed: Invalid format after decoding.",
        decodedData,
        input,
      );
      throw new Error("Decryption format is invalid.");
    }

    return decryptedArray[0]; // Return the original input without the salt
  } catch (error) {
    console.error("Decryption failed:", error);
    return null; // Return null on failure
  }
};

// Use getDecryptedLocalStorageValue for full retrieval, decryption, and parsing from localStorage.
// decrypted localstorage function
export const getDecryptedLocalStorageValue = (key) => {
  try {
    const encryptedValue = localStorage.getItem(key);
    if (!encryptedValue) return false;
    const decryptedValue = apiDecrypt(encryptedValue); // Decrypt
    return JSON.parse(decryptedValue); // Parse JSON
  } catch (error) {
    console.error(
      `Failed to parse or decrypt localStorage key "${key}":`,
      error,
    );
    return false;
  }
};

// -> Key Differences
// 1.apiDecrypt	      2.getDecryptedLocalStorageValue

// * Primary Purpose
// 1. Decrypts an encrypted string.
// 2. Retrieves, decrypts, and parses localStorage.

// * Input
// 1. An encrypted string.
// 1. A localStorage key and a default value.

// * Output
// 1. Decrypted string.
// 2. Parsed JSON or a default value.

// * Error Handling
// 1. Limited to returning [] on failure.
// 2. Logs errors and falls back to a default value.
